export const PROFILE_FRAME_IDS = [
  'frame_basic',
  'frame_bronze',
  'frame_gold',
  'frame_sakura',
  'frame_flame',
  'frame_wave',
  'frame_storm',
  'frame_dragon',
  'frame_knight',
  'frame_neko',
  'frame_neon',
  'frame_silver',
  'frame_arcane',
] as const;

export const PROFILE_BANNERS = [
  {
    id: 'dawn',
    label: 'Рассвет',
    src: '/art/profile/guildrise-profile-dawn-1024.webp',
    alt: 'Горная цитадель над облаками на рассвете',
  },
  {
    id: 'clouds',
    label: 'Облачное море',
    src: '/art/profile/guildrise-profile-clouds.webp',
    alt: 'Облачное море и горы на рассвете',
  },
] as const;

/**
 * Фоны под блоками. Арт лежит в `/art/profile/`, ширина 1024 — на телефоне
 * хватает с запасом под 2x. `tone` подкрашивает слой через hue-rotate.
 */
export const PROFILE_BACKGROUNDS = [
  {
    id: 'duet',
    label: 'Дуэт',
    src640: '/art/profile/guildrise-profile-duet-v2-480.webp',
    src1024: '/art/profile/guildrise-profile-duet-v2-832.webp',
    alt: 'Две аниме-фигуры в матовой дымке по краям',
  },
  {
    id: 'hall',
    label: 'Зал',
    src640: '/art/profile/guildrise-profile-hall-v2-480.webp',
    src1024: '/art/profile/guildrise-profile-hall-v2-832.webp',
    alt: 'Ночной зал гильдии в тумане',
  },
  {
    id: 'portrait',
    label: 'Портрет',
    src640: '/art/profile/guildrise-profile-portrait-v2-480.webp',
    src1024: '/art/profile/guildrise-profile-portrait-v2-832.webp',
    alt: 'Портрет аниме-девушки в матовой дымке',
  },
] as const;

export interface ProfileCosmetics {
  readonly version: 1;
  /** null preserves the current server frame, including future catalog additions. */
  readonly frameId: (typeof PROFILE_FRAME_IDS)[number] | null;
  readonly bannerId: (typeof PROFILE_BANNERS)[number]['id'];
  readonly backgroundId: (typeof PROFILE_BACKGROUNDS)[number]['id'];
  readonly frameMotion: boolean;
  readonly backgroundMotion: boolean;
}

const DEFAULT_COSMETICS: ProfileCosmetics = {
  version: 1,
  frameId: null,
  bannerId: 'dawn',
  backgroundId: 'duet',
  frameMotion: true,
  backgroundMotion: true,
};

function validateCosmetics(value: unknown): ProfileCosmetics | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const data = value as Record<string, unknown>;
  const frameId = PROFILE_FRAME_IDS.find((id) => id === data.frameId);
  const banner = PROFILE_BANNERS.find(({ id }) => id === data.bannerId);
  const background = PROFILE_BACKGROUNDS.find(({ id }) => id === data.backgroundId);
  if (
    data.version !== 1 ||
    (data.frameId !== null && frameId === undefined) ||
    banner === undefined ||
    background === undefined ||
    typeof data.frameMotion !== 'boolean' ||
    typeof data.backgroundMotion !== 'boolean'
  )
    return null;

  // Explicit projection: storage can never supply server identity or asset URLs.
  return {
    version: 1,
    frameId: frameId ?? null,
    bannerId: banner.id,
    backgroundId: background.id,
    frameMotion: data.frameMotion,
    backgroundMotion: data.backgroundMotion,
  };
}

function storageKey(guildId: string): string {
  return `guildrise:profile:v1:${guildId}`;
}

export function readProfileCosmetics(
  guildId: string,
  getStorage: () => Pick<Storage, 'getItem'> = () => window.localStorage,
): ProfileCosmetics {
  try {
    const raw = getStorage().getItem(storageKey(guildId));
    return (raw === null ? null : validateCosmetics(JSON.parse(raw))) ?? DEFAULT_COSMETICS;
  } catch {
    return DEFAULT_COSMETICS;
  }
}

export function saveProfileCosmetics(
  guildId: string,
  cosmetics: ProfileCosmetics,
  getStorage: () => Pick<Storage, 'setItem'> = () => window.localStorage,
): boolean {
  const validated = validateCosmetics(cosmetics);
  if (validated === null) return false;
  try {
    getStorage().setItem(storageKey(guildId), JSON.stringify(validated));
    return true;
  } catch {
    return false;
  }
}
